import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let app = null;

function isMessagingSupported() {
  // Quick browser-level capability check.
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    typeof window.indexedDB !== 'undefined'
  );
}

async function isMessagingActuallySupported() {
  // Firebase's own check correctly reports iOS Safari / unsupported browsers.
  if (!isMessagingSupported()) return false;
  try {
    const { isSupported } = await import('firebase/messaging');
    return await isSupported();
  } catch {
    return false;
  }
}

export function initFirebase() {
  if (!app) {
    try {
      app = initializeApp(firebaseConfig);
    } catch {
      app = null;
    }
  }
  return app;
}

export async function requestNotificationPermission(api) {
  if (!(await isMessagingActuallySupported())) {
    console.log('Push notifications not supported in this browser');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    const firebaseApp = initFirebase();
    if (!firebaseApp) return null;
    const { getMessaging, getToken } = await import('firebase/messaging');
    const messaging = getMessaging(firebaseApp);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (token) {
      await api.post('/auth/fcm-token', { token });
      console.log('FCM token registered');
      return token;
    }
  } catch (error) {
    console.log('Push notifications not available:', error.message);
  }
  return null;
}

export function onForegroundMessage(callback) {
  if (!isMessagingSupported()) return null;

  isMessagingActuallySupported().then((ok) => {
    if (!ok) return;
    try {
      const firebaseApp = initFirebase();
      if (!firebaseApp) return;
      import('firebase/messaging').then(({ getMessaging, onMessage }) => {
        try {
          const messaging = getMessaging(firebaseApp);
          onMessage(messaging, (payload) => {
            callback(payload);
          });
        } catch (e) {
          console.log('onMessage not supported:', e?.message);
        }
      }).catch(() => {});
    } catch (error) {
      console.log('Foreground messages not supported');
    }
  });
  return null;
}
